import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageToggle from '../../components/feature/LanguageToggle';

export default function HomePage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [email, setEmail] = useState('');
  const { t } = useTranslation();

  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', () => {});
  }

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    alert('Thank you for subscribing!');
    setEmail('');
  };

  const expertiseCards = [
    {
      icon: 'ri-links-line',
      title: t('home_exp_c1_title'),
      desc: t('home_exp_c1_desc'),
      tags: [t('home_exp_c1_t1'), t('home_exp_c1_t2'), t('home_exp_c1_t3')],
    },
    {
      icon: 'ri-lightbulb-line',
      title: t('home_exp_c2_title'),
      listItems: [t('home_exp_c2_l1'), t('home_exp_c2_l2'), t('home_exp_c2_l3'), t('home_exp_c2_l4')],
      tags: [t('home_exp_c2_t1'), t('home_exp_c2_t2'), t('home_exp_c2_t3')],
    },
    {
      icon: 'ri-funds-line',
      title: t('home_exp_c3_title'),
      desc: t('home_exp_c3_desc'),
      tags: [t('home_exp_c3_t1'), t('home_exp_c3_t2'), t('home_exp_c3_t3')],
    },
    {
      icon: 'ri-building-line',
      title: t('home_exp_c4_title'),
      listItems: [t('home_exp_c4_l1'), t('home_exp_c4_l2'), t('home_exp_c4_l3'), t('home_exp_c4_l4')],
      tags: [t('home_exp_c4_t1'), t('home_exp_c4_t2'), t('home_exp_c4_t3')],
    },
  ];

  const whyItems = [
    { number: '01', title: t('home_why_01_title'), desc: t('home_why_01_desc') },
    { number: '02', title: t('home_why_02_title'), desc: t('home_why_02_desc') },
    { number: '03', title: t('home_why_03_title'), desc: t('home_why_03_desc') },
    { number: '04', title: t('home_why_04_title'), desc: t('home_why_04_desc') },
    { number: '05', title: t('home_why_05_title'), desc: t('home_why_05_desc') },
    { number: '06', title: t('home_why_06_title'), desc: t('home_why_06_desc') },
  ];

  const partnerStats = [
    { icon: 'ri-government-line', value: t('home_part_s1_value'), label: t('home_part_s1_label'), sub: t('home_part_s1_sub') },
    { icon: 'ri-global-line', value: t('home_part_s2_value'), label: t('home_part_s2_label'), sub: t('home_part_s2_sub') },
    { icon: 'ri-briefcase-4-line', value: t('home_part_s3_value'), label: t('home_part_s3_label'), sub: t('home_part_s3_sub') },
    { icon: 'ri-map-2-line', value: t('home_part_s4_value'), label: t('home_part_s4_label'), sub: t('home_part_s4_sub') },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img src="https://static.readdy.ai/image/23290c60640f0a2a61534c667a486e48/08e651986c1a2c862c6b7b398d1365c8.jpeg" alt="GDSATLAS Logo" className="h-12 w-auto" />
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#about" className="text-sm font-medium text-gray-700 hover:text-[#D4AF37] transition-colors whitespace-nowrap">{t('home_nav_about')}</a>
              <a href="#subsidiaries" className="text-sm font-medium text-gray-700 hover:text-[#D4AF37] transition-colors whitespace-nowrap">{t('home_nav_companies')}</a>
              <a href="#expertise" className="text-sm font-medium text-gray-700 hover:text-[#D4AF37] transition-colors whitespace-nowrap">{t('home_nav_services')}</a>
              <a href="#investment" className="text-sm font-medium text-gray-700 hover:text-[#D4AF37] transition-colors whitespace-nowrap">{t('home_nav_investment')}</a>
              <a href="#contact" className="text-sm font-medium text-gray-700 hover:text-[#D4AF37] transition-colors whitespace-nowrap">{t('home_nav_contact')}</a>
              <LanguageToggle />
            </div>
            <button className="md:hidden cursor-pointer" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              <i className="ri-menu-line text-2xl text-gray-700"></i>
            </button>
          </div>
          {isMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-3">
              <a href="#about" className="block text-sm font-medium text-gray-700 hover:text-[#D4AF37]">{t('home_nav_about')}</a>
              <a href="#subsidiaries" className="block text-sm font-medium text-gray-700 hover:text-[#D4AF37]">{t('home_nav_companies')}</a>
              <a href="#expertise" className="block text-sm font-medium text-gray-700 hover:text-[#D4AF37]">{t('home_nav_services')}</a>
              <a href="#investment" className="block text-sm font-medium text-gray-700 hover:text-[#D4AF37]">{t('home_nav_investment')}</a>
              <a href="#contact" className="block text-sm font-medium text-gray-700 hover:text-[#D4AF37]">{t('home_nav_contact')}</a>
              <LanguageToggle className="mt-2" />
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/33aa4ada7ee49bf14fe2e5cb0529f41b.jpg"
            alt="African Port"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A1F44]/85 via-[#0A1F44]/75 to-[#0A1F44]/85"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center w-full">
          <div className="inline-flex items-center px-6 py-2 bg-[#0A1F44]/60 backdrop-blur-sm rounded-full mb-8">
            <i className="ri-shield-check-line text-[#D4AF37] text-lg mr-2"></i>
            <span className="text-white text-sm font-medium">{t('home_hero_badge')}</span>
          </div>
          <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t('home_hero_title').split('\n').map((line, i) => (
              <span key={i}>{line}{i === 0 && <br />}</span>
            ))}
          </h1>
          <p className="text-xl text-gray-200 mb-12 max-w-3xl mx-auto">{t('home_hero_subtitle')}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="#subsidiaries" className="px-8 py-4 bg-[#D4AF37] text-[#0A1F44] rounded-full text-base font-semibold hover:bg-[#C4A037] transition-all flex items-center whitespace-nowrap">
              {t('home_hero_cta1')}
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
            <a href="#investment" className="px-8 py-4 border-2 border-white text-white rounded-full text-base font-semibold hover:bg-white hover:text-[#0A1F44] transition-all whitespace-nowrap">
              {t('home_hero_cta2')}
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-[#D4AF37]/20 rounded-full mb-6">
                <span className="text-[#0A1F44] text-sm font-semibold">{t('home_about_tag')}</span>
              </div>
              <h2 className="text-5xl font-bold text-[#0A1F44] mb-6 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t('home_about_title')}
              </h2>
              <p className="text-gray-600 text-base leading-relaxed mb-8">{t('home_about_desc')}</p>
              <div className="bg-[#FFF9F0] border-l-4 border-[#D4AF37] p-6 rounded-lg">
                <p className="text-[#0A1F44] font-medium text-base leading-relaxed">{t('home_about_quote')}</p>
              </div>
            </div>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20African%20business%20leaders%20shaking%20hands%20in%20modern%20conference%20room%2C%20diverse%20international%20partnership%20meeting%2C%20contemporary%20office%20setting%20with%20large%20windows%2C%20natural%20lighting%2C%20confident%20executives%20in%20business%20attire%2C%20symbolizing%20trust%20and%20collaboration%2C%20high-quality%20corporate%20photography%20with%20warm%20professional%20atmosphere&width=800&height=900&seq=about-img-001&orientation=portrait"
                  alt="Business Partnership"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute top-6 right-6 bg-white rounded-full w-24 h-24 flex flex-col items-center justify-center">
                  <i className="ri-global-line text-[#D4AF37] text-3xl mb-1"></i>
                  <span className="text-[#0A1F44] text-xs font-bold">{t('home_about_badge')}</span>
                  <span className="text-gray-600 text-xs">{t('home_about_badge_sub')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Expertise Section */}
      <section id="expertise" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-[#D4AF37]/20 rounded-full mb-4">
              <span className="text-[#0A1F44] text-sm font-semibold">{t('home_exp_tag')}</span>
            </div>
            <h2 className="text-5xl font-bold text-[#0A1F44] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{t('home_exp_title')}</h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">{t('home_exp_subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {expertiseCards.map((card, idx) => (
              <div key={idx} className="bg-[#F8F9FA] border border-gray-100 rounded-3xl p-8 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-pointer group">
                <div className="w-16 h-16 bg-[#0A1F44] rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#D4AF37] transition-colors duration-300">
                  <i className={`${card.icon} text-[#D4AF37] text-2xl group-hover:text-[#0A1F44]`}></i>
                </div>
                <h3 className="text-2xl font-bold text-[#0A1F44] mb-4">{card.title}</h3>
                {card.desc && <p className="text-gray-600 text-sm leading-relaxed mb-6">{card.desc}</p>}
                {card.listItems && (
                  <ul className="text-gray-600 text-sm leading-relaxed space-y-3 mb-6">
                    {card.listItems.map((item) => (
                      <li key={item} className="flex items-start">
                        <i className="ri-check-line text-[#D4AF37] mr-2 mt-0.5 flex-shrink-0"></i>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex flex-wrap gap-2">
                  {card.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-[#D4AF37]/15 text-[#0A1F44] text-xs font-medium rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Partnership Capacities Section */}
      <section id="partnerships" className="py-24 bg-[#F8F9FA] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-[#D4AF37]/20 rounded-full mb-4">
              <span className="text-[#0A1F44] text-sm font-semibold">{t('home_part_tag')}</span>
            </div>
            <h2 className="text-5xl font-bold text-[#0A1F44] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{t('home_part_title')}</h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">{t('home_part_subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-6">
            <div className="md:col-span-2 relative rounded-3xl overflow-hidden h-72 group">
              <img
                src="https://readdy.ai/api/search-image?query=High-level%20international%20business%20summit%20in%20Africa%2C%20diverse%20group%20of%20executives%20and%20government%20officials%20gathered%20around%20a%20large%20conference%20table%2C%20modern%20boardroom%20with%20panoramic%20city%20views%2C%20professional%20diplomatic%20meeting%20atmosphere%2C%20warm%20ambient%20lighting%2C%20suits%20and%20formal%20attire%2C%20documents%20and%20presentations%2C%20symbolizing%20strategic%20alliances%20and%20high-stakes%20negotiations%2C%20clean%20minimal%20background&width=1000&height=600&seq=partnership-main-001&orientation=landscape"
                alt="Strategic Alliances"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F44]/90 via-[#0A1F44]/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-8">
                <div className="flex items-center mb-3">
                  <div className="w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center mr-3">
                    <i className="ri-handshake-line text-[#0A1F44] text-lg"></i>
                  </div>
                  <span className="text-[#D4AF37] text-sm font-semibold uppercase tracking-wider">{t('home_part_f1_tag')}</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{t('home_part_f1_title')}</h3>
                <p className="text-white/80 text-sm leading-relaxed max-w-lg">{t('home_part_f1_desc')}</p>
              </div>
            </div>
            <div className="relative rounded-3xl overflow-hidden h-72 group">
              <img
                src="https://readdy.ai/api/search-image?query=Global%20investor%20meeting%20in%20a%20sleek%20modern%20office%2C%20financial%20charts%20on%20screens%2C%20international%20businesspeople%20reviewing%20investment%20portfolios%2C%20professional%20atmosphere%20with%20clean%20white%20and%20gold%20accents%2C%20natural%20light%2C%20confident%20expressions%2C%20symbolizing%20capital%20mobilization%20and%20global%20finance%2C%20minimal%20elegant%20background&width=600&height=600&seq=partnership-invest-001&orientation=squarish"
                alt="Global Investors"
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F44]/90 via-[#0A1F44]/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-6">
                <div className="flex items-center mb-2">
                  <div className="w-8 h-8 bg-[#D4AF37] rounded-full flex items-center justify-center mr-2">
                    <i className="ri-global-line text-[#0A1F44] text-sm"></i>
                  </div>
                  <span className="text-[#D4AF37] text-xs font-semibold uppercase tracking-wider">{t('home_part_f2_tag')}</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{t('home_part_f2_title')}</h3>
                <p className="text-white/80 text-xs leading-relaxed">{t('home_part_f2_desc')}</p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { img: "https://readdy.ai/api/search-image?query=African%20infrastructure%20construction%20project%2C%20large-scale%20road%20or%20bridge%20being%20built%20in%20a%20savanna%20landscape%2C%20heavy%20machinery%20and%20construction%20workers%2C%20aerial%20perspective%2C%20golden%20hour%20lighting%2C%20symbolizing%20development%20and%20progress%2C%20clean%20sky%20background%20with%20warm%20tones%2C%20professional%20industrial%20photography&width=600&height=500&seq=partnership-infra-001&orientation=squarish", icon: 'ri-building-4-line', tagKey: 'home_part_f3_tag', titleKey: 'home_part_f3_title' },
              { img: "https://readdy.ai/api/search-image?query=Energy%20sector%20partnership%20in%20Africa%2C%20solar%20panels%20and%20wind%20turbines%20in%20a%20vast%20open%20landscape%2C%20clean%20energy%20infrastructure%2C%20technicians%20inspecting%20equipment%2C%20bright%20blue%20sky%20with%20white%20clouds%2C%20symbolizing%20sustainable%20energy%20development%2C%20professional%20photography%20with%20warm%20natural%20tones%2C%20minimal%20background&width=600&height=500&seq=partnership-energy-001&orientation=squarish", icon: 'ri-flashlight-line', tagKey: 'home_part_f4_tag', titleKey: 'home_part_f4_title' },
              { img: "https://readdy.ai/api/search-image?query=Agricultural%20development%20in%20West%20Africa%2C%20vast%20green%20farmland%20with%20modern%20irrigation%20systems%2C%20farmers%20and%20agronomists%20working%20together%2C%20aerial%20view%20of%20organized%20crop%20fields%2C%20warm%20golden%20light%2C%20symbolizing%20food%20security%20and%20agribusiness%20growth%2C%20professional%20photography%20with%20lush%20green%20and%20earth%20tones%2C%20clean%20sky%20background&width=600&height=500&seq=partnership-agri-001&orientation=squarish", icon: 'ri-plant-line', tagKey: 'home_part_f5_tag', titleKey: 'home_part_f5_title' },
            ].map((item, idx) => (
              <div key={idx} className="relative rounded-3xl overflow-hidden h-64 group">
                <img src={item.img} alt={t(item.titleKey)} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F44]/90 via-[#0A1F44]/40 to-transparent"></div>
                <div className="absolute bottom-0 left-0 p-6">
                  <div className="flex items-center mb-2">
                    <div className="w-8 h-8 bg-[#D4AF37] rounded-full flex items-center justify-center mr-2">
                      <i className={`${item.icon} text-[#0A1F44] text-sm`}></i>
                    </div>
                    <span className="text-[#D4AF37] text-xs font-semibold uppercase tracking-wider">{t(item.tagKey)}</span>
                  </div>
                  <h3 className="text-lg font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>{t(item.titleKey)}</h3>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-[#0A1F44] rounded-3xl p-10 grid grid-cols-2 md:grid-cols-4 gap-8">
            {partnerStats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${stat.icon} text-[#D4AF37] text-xl`}></i>
                </div>
                <p className="text-4xl font-bold text-[#D4AF37] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{stat.value}</p>
                <p className="text-white font-semibold text-sm mb-1">{stat.label}</p>
                <p className="text-white/50 text-xs">{stat.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Subsidiaries Section */}
      <section id="subsidiaries" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-[#D4AF37]/20 rounded-full mb-4">
              <span className="text-[#0A1F44] text-sm font-semibold">{t('home_sub_tag')}</span>
            </div>
            <h2 className="text-5xl font-bold text-[#0A1F44] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{t('home_sub_title')}</h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">{t('home_sub_subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <Link to="/compass-resources" className="group">
              <div className="bg-gradient-to-br from-[#0A1F44] to-[#0A1F44]/90 rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer">
                <div className="relative h-80 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Open%20pit%20gold%20mine%20in%20Africa%20with%20massive%20excavation%20equipment%2C%20aerial%20view%20of%20terraced%20mining%20operations%2C%20golden%20sunset%20lighting%20over%20mineral%20extraction%20site%2C%20professional%20industrial%20photography%20showing%20scale%20and%20precision%2C%20modern%20mining%20machinery%2C%20rich%20earth%20tones%2C%20sustainable%20resource%20development%2C%20dramatic%20landscape%20with%20mining%20infrastructure&width=800&height=600&seq=compass-mining-001&orientation=landscape"
                    alt="Compass Resources Mining"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F44] via-[#0A1F44]/50 to-transparent"></div>
                  <div className="absolute top-6 left-6">
                    <img src="https://static.readdy.ai/image/23290c60640f0a2a61534c667a486e48/829f51ab4aab91f5690d98faedf27d9e.png" alt="Compass Resources Logo" className="h-16 w-auto" />
                  </div>
                </div>
                <div className="p-8">
                  <h3 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{t('home_compass_title')}</h3>
                  <p className="text-white/80 text-base leading-relaxed mb-6">{t('home_compass_desc')}</p>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                      <p className="text-[#D4AF37] text-2xl font-bold">{t('home_compass_stat1')}</p>
                      <p className="text-white/70 text-sm">{t('home_compass_stat1_label')}</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                      <p className="text-[#D4AF37] text-2xl font-bold">{t('home_compass_stat2')}</p>
                      <p className="text-white/70 text-sm">{t('home_compass_stat2_label')}</p>
                    </div>
                  </div>
                  <div className="flex items-center text-[#D4AF37] font-semibold text-base group-hover:gap-3 transition-all">
                    {t('home_compass_cta')}
                    <i className="ri-arrow-right-line ml-2 group-hover:translate-x-2 transition-transform"></i>
                  </div>
                </div>
              </div>
            </Link>
            <Link to="/mauritrans-shipping" className="group">
              <div className="bg-gradient-to-br from-[#0A1F44] to-[#0A1F44]/90 rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer">
                <div className="relative h-80 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20cargo%20ship%20at%20African%20port%20terminal%20with%20containers%20being%20loaded%20by%20massive%20cranes%2C%20professional%20maritime%20logistics%20photography%2C%20bustling%20port%20operations%20at%20golden%20hour%2C%20organized%20container%20stacks%2C%20efficient%20supply%20chain%20management%2C%20contemporary%20shipping%20infrastructure%2C%20deep%20blue%20ocean%2C%20international%20trade%20and%20commerce%2C%20high-quality%20industrial%20photography&width=800&height=600&seq=mauritrans-shipping-001&orientation=landscape"
                    alt="Mauritrans Shipping"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F44] via-[#0A1F44]/50 to-transparent"></div>
                  <div className="absolute top-6 left-6 bg-white/95 backdrop-blur-sm px-4 py-3 rounded-xl">
                    <img src="https://static.readdy.ai/image/23290c60640f0a2a61534c667a486e48/6ccaa6d0175bf43f4647618f6ac0b19b.png" alt="Mauritrans Shipping Logo" className="h-10 w-auto" />
                  </div>
                </div>
                <div className="p-8">
                  <h3 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{t('home_mauritrans_title')}</h3>
                  <p className="text-white/80 text-base leading-relaxed mb-6">{t('home_mauritrans_desc')}</p>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                      <p className="text-[#D4AF37] text-2xl font-bold">{t('home_mauritrans_stat1')}</p>
                      <p className="text-white/70 text-sm">{t('home_mauritrans_stat1_label')}</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                      <p className="text-[#D4AF37] text-2xl font-bold">{t('home_mauritrans_stat2')}</p>
                      <p className="text-white/70 text-sm">{t('home_mauritrans_stat2_label')}</p>
                    </div>
                  </div>
                  <div className="flex items-center text-[#D4AF37] font-semibold text-base group-hover:gap-3 transition-all">
                    {t('home_mauritrans_cta')}
                    <i className="ri-arrow-right-line ml-2 group-hover:translate-x-2 transition-transform"></i>
                  </div>
                </div>
              </div>
            </Link>
          </div>
          {/* Trans-Fish — full width card */}
          <a href="https://www.trans-fish.com" target="_blank" rel="noopener noreferrer nofollow" className="group block mt-8">
            <div className="bg-gradient-to-br from-[#0A1F44] to-[#0A1F44]/90 rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer">
              <div className="grid md:grid-cols-2">
                <div className="relative h-72 md:h-auto overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Mauritanian%20fishing%20boats%20on%20the%20Atlantic%20Ocean%20at%20sunrise%2C%20traditional%20and%20modern%20fishing%20vessels%20in%20a%20busy%20harbor%2C%20fishermen%20unloading%20fresh%20catch%2C%20vibrant%20coastal%20scene%20with%20golden%20morning%20light%20reflecting%20on%20calm%20blue%20water%2C%20nets%20and%20equipment%20on%20deck%2C%20professional%20maritime%20photography%2C%20rich%20ocean%20blues%20and%20warm%20amber%20tones%2C%20sustainable%20fisheries%20and%20seafood%20industry%2C%20clean%20minimal%20sky%20background&width=800&height=600&seq=transfish-main-001&orientation=landscape"
                    alt="Trans-Fish Fisheries"
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0A1F44]/60"></div>
                  <div className="absolute top-6 left-6 bg-white/95 backdrop-blur-sm px-4 py-3 rounded-xl">
                    <img src="https://storage.readdy-site.link/project_files/dbb39942-035d-43aa-b3f6-8452bcee8349/a42d5a63-375b-4f18-b20d-82a3f05e3ad5_transfish_logo_png_-removebg-preview.png?v=fcbfbb2ad23de348d97c1af6040a288f" alt="Trans-Fish Logo" className="h-12 w-auto" />
                  </div>
                </div>
                <div className="p-8 flex flex-col justify-center">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#D4AF37]/20 rounded-full mb-4 w-fit">
                    <i className="ri-anchor-line text-[#D4AF37] text-sm"></i>
                    <span className="text-[#D4AF37] text-xs font-semibold uppercase tracking-wider">Fisheries &amp; Seafood</span>
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{t('home_transfish_title')}</h3>
                  <p className="text-white/80 text-base leading-relaxed mb-6">{t('home_transfish_desc')}</p>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                      <p className="text-[#D4AF37] text-2xl font-bold">{t('home_transfish_stat1')}</p>
                      <p className="text-white/70 text-sm">{t('home_transfish_stat1_label')}</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                      <p className="text-[#D4AF37] text-2xl font-bold">{t('home_transfish_stat2')}</p>
                      <p className="text-white/70 text-sm">{t('home_transfish_stat2_label')}</p>
                    </div>
                  </div>
                  <div className="flex items-center text-[#D4AF37] font-semibold text-base group-hover:gap-3 transition-all">
                    {t('home_transfish_cta')}
                    <i className="ri-external-link-line ml-2 group-hover:translate-x-2 transition-transform"></i>
                  </div>
                </div>
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* Investment CTA Section */}
      <section id="investment" className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20African%20city%20skyline%20at%20golden%20hour%20sunset%2C%20contemporary%20skyscrapers%20and%20urban%20development%2C%20warm%20orange%20and%20purple%20sky%2C%20silhouette%20of%20growing%20metropolis%2C%20professional%20architectural%20photography%20showing%20economic%20progress%2C%20vibrant%20cityscape%20with%20construction%20cranes%20symbolizing%20growth%2C%20inspiring%20business%20atmosphere%20with%20dramatic%20lighting&width=1920&height=800&seq=investment-bg-001&orientation=landscape"
            alt="Investment Opportunities"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#001E3C]/85"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end">
            <div className="mb-12 md:mb-0">
              <h2 className="text-6xl md:text-7xl font-bold text-white mb-8 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t('home_inv_title').split('\n').map((line, i) => (
                  <span key={i}>{line}{i === 0 && <br />}</span>
                ))}
              </h2>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#contact" className="inline-flex items-center px-8 py-4 bg-[#D4AF37] text-[#0A1F44] rounded-full text-base font-semibold hover:bg-[#C4A037] transition-all whitespace-nowrap">
                  {t('home_inv_cta1')}
                  <div className="ml-3 w-8 h-8 bg-[#0A1F44] rounded-full flex items-center justify-center">
                    <i className="ri-arrow-right-line text-white text-sm"></i>
                  </div>
                </a>
                <a href="#contact" className="inline-flex items-center px-8 py-4 border-2 border-white text-white rounded-full text-base font-semibold hover:bg-white hover:text-[#0A1F44] transition-all whitespace-nowrap">
                  {t('home_inv_cta2')}
                </a>
              </div>
            </div>
            <div className="text-right">
              <p className="text-white text-xl leading-relaxed max-w-xl">
                {t('home_inv_desc').split('\n').map((line, i) => (
                  <span key={i}>{line}{i === 0 && <br />}</span>
                ))}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-us" className="py-24 bg-[#FFF9F0]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-[#D4AF37]/20 rounded-full mb-4">
              <span className="text-[#0A1F44] text-sm font-semibold">{t('home_why_tag')}</span>
            </div>
            <h2 className="text-5xl font-bold text-[#0A1F44] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{t('home_why_title')}</h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">{t('home_why_subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {whyItems.map((item, index) => (
              <div key={index} className="bg-white rounded-3xl p-8 hover:shadow-xl transition-all duration-300 cursor-pointer">
                <div className="text-6xl font-bold text-[#D4AF37]/30 mb-4">{item.number}</div>
                <h3 className="text-xl font-bold text-[#0A1F44] mb-3">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-[#D4AF37]/20 rounded-full mb-4">
              <span className="text-[#0A1F44] text-sm font-semibold">{t('home_contact_tag')}</span>
            </div>
            <h2 className="text-5xl font-bold text-[#0A1F44] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{t('home_contact_title')}</h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">{t('home_contact_subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-map-pin-line text-[#D4AF37] text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-[#0A1F44] mb-2">{t('home_contact_addr_label')}</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">ZRF ND F Nord Sect1 - 444<br />Tevragh Zeina<br />Nouakchott – Mauritanie</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-phone-line text-[#D4AF37] text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-[#0A1F44] mb-2">{t('home_contact_phone_label')}</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">+(222) 46427000<br />+(222) 44290990</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-mail-line text-[#D4AF37] text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-[#0A1F44] mb-2">{t('home_contact_email_label')}</h4>
                    <p className="text-gray-600 text-sm">contact@gds-atlas.com</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-global-line text-[#D4AF37] text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-[#0A1F44] mb-2">{t('home_contact_web_label')}</h4>
                    <a href="https://www.gds-atlas.com" target="_blank" rel="noopener noreferrer nofollow" className="text-[#D4AF37] text-sm hover:underline">www.gds-atlas.com</a>
                  </div>
                </div>
                <div className="bg-[#FFF9F0] border-l-4 border-[#D4AF37] p-6 rounded-lg mt-8">
                  <h4 className="text-lg font-semibold text-[#0A1F44] mb-3">{t('home_contact_subs_title')}</h4>
                  <div className="space-y-2">
                    <Link to="/compass-resources" className="flex items-center text-gray-600 text-sm hover:text-[#D4AF37] transition-colors">
                      <i className="ri-arrow-right-s-line mr-2"></i>
                      {t('home_contact_compass')}
                    </Link>
                    <Link to="/mauritrans-shipping" className="flex items-center text-gray-600 text-sm hover:text-[#D4AF37] transition-colors">
                      <i className="ri-arrow-right-s-line mr-2"></i>
                      {t('home_contact_mauritrans')}
                    </Link>
                    <a href="https://www.trans-fish.com" target="_blank" rel="noopener noreferrer nofollow" className="flex items-center text-gray-600 text-sm hover:text-[#D4AF37] transition-colors">
                      <i className="ri-arrow-right-s-line mr-2"></i>
                      {t('home_contact_transfish')}
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-[#F8F9FA] rounded-3xl p-8">
              <h3 className="text-2xl font-bold text-[#0A1F44] mb-6">{t('home_form_title')}</h3>
              <form className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('home_form_name')}</label>
                  <input type="text" className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm" placeholder={t('home_form_name_ph')} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('home_form_email')}</label>
                  <input type="email" className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm" placeholder={t('home_form_email_ph')} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('home_form_message')}</label>
                  <textarea rows={5} className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm resize-none" placeholder={t('home_form_message_ph')}></textarea>
                </div>
                <button type="submit" className="w-full px-8 py-4 bg-[#D4AF37] text-[#0A1F44] rounded-full text-base font-semibold hover:bg-[#C4A037] transition-all whitespace-nowrap cursor-pointer">
                  {t('home_form_submit')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A1F44] py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-5 gap-12 mb-12">
            <div className="md:col-span-2">
              <img src="https://static.readdy.ai/image/23290c60640f0a2a61534c667a486e48/08e651986c1a2c862c6b7b398d1365c8.jpeg" alt="GDSATLAS Logo" className="h-12 w-auto mb-6" />
              <p className="text-white/70 text-sm mb-8 leading-relaxed">{t('home_footer_tagline')}</p>
              <div className="mb-6">
                <p className="text-white text-sm font-semibold mb-3">{t('home_footer_subscribe')}</p>
                <form onSubmit={handleSubscribe} className="flex">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('home_footer_subscribe_ph')}
                    className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-l-lg text-white text-sm placeholder-white/50 focus:outline-none focus:border-[#D4AF37]"
                    required
                  />
                  <button type="submit" className="px-6 py-2 bg-[#D4AF37] text-[#0A1F44] rounded-r-lg text-sm font-semibold hover:bg-[#C4A037] transition-all whitespace-nowrap cursor-pointer">
                    {t('home_footer_subscribe_btn')}
                  </button>
                </form>
              </div>
            </div>
            <div>
              <h4 className="text-[#D4AF37] text-sm font-bold mb-4">{t('home_footer_companies')}</h4>
              <ul className="space-y-3">
                <li><Link to="/compass-resources" className="text-white/70 text-sm hover:text-[#D4AF37] transition-colors">Compass Resources</Link></li>
                <li><Link to="/mauritrans-shipping" className="text-white/70 text-sm hover:text-[#D4AF37] transition-colors">Mauritrans Shipping</Link></li>
                <li><a href="https://www.trans-fish.com" target="_blank" rel="noopener noreferrer nofollow" className="text-white/70 text-sm hover:text-[#D4AF37] transition-colors">Trans-Fish</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-[#D4AF37] text-sm font-bold mb-4">{t('home_footer_services')}</h4>
              <ul className="space-y-3">
                <li><a href="#expertise" className="text-white/70 text-sm hover:text-[#D4AF37] transition-colors">{t('home_footer_svc1')}</a></li>
                <li><a href="#expertise" className="text-white/70 text-sm hover:text-[#D4AF37] transition-colors">{t('home_footer_svc2')}</a></li>
                <li><a href="#expertise" className="text-white/70 text-sm hover:text-[#D4AF37] transition-colors">{t('home_footer_svc3')}</a></li>
                <li><a href="#expertise" className="text-white/70 text-sm hover:text-[#D4AF37] transition-colors">{t('home_footer_svc4')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-[#D4AF37] text-sm font-bold mb-4">{t('home_footer_contact')}</h4>
              <ul className="space-y-3">
                <li className="flex items-start text-white/70 text-sm"><i className="ri-map-pin-line text-[#D4AF37] mr-2 mt-1"></i><span>Nouakchott, Mauritanie</span></li>
                <li className="flex items-start text-white/70 text-sm"><i className="ri-phone-line text-[#D4AF37] mr-2 mt-1"></i><span>+(222) 46427000</span></li>
                <li className="flex items-start text-white/70 text-sm"><i className="ri-mail-line text-[#D4AF37] mr-2 mt-1"></i><span>contact@gds-atlas.com</span></li>
                <li className="flex items-start text-white/70 text-sm"><i className="ri-global-line text-[#D4AF37] mr-2 mt-1"></i><a href="https://www.gds-atlas.com" target="_blank" rel="noopener noreferrer nofollow" className="hover:text-[#D4AF37] transition-colors">www.gds-atlas.com</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-white/50 text-sm mb-4 md:mb-0">{t('home_footer_copyright')}</p>
              <div className="flex items-center space-x-4">
                <a href="#" className="w-10 h-10 flex items-center justify-center text-[#D4AF37] hover:bg-white/10 rounded-full transition-all"><i className="ri-linkedin-fill text-xl"></i></a>
                <a href="#" className="w-10 h-10 flex items-center justify-center text-[#D4AF37] hover:bg-white/10 rounded-full transition-all"><i className="ri-twitter-fill text-xl"></i></a>
                <a href="#" className="w-10 h-10 flex items-center justify-center text-[#D4AF37] hover:bg-white/10 rounded-full transition-all"><i className="ri-facebook-fill text-xl"></i></a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/22244290990"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center hover:bg-[#1ebe5d] hover:scale-110 transition-all duration-300 cursor-pointer"
        aria-label="Chat on WhatsApp"
      >
        <i className="ri-whatsapp-line text-white text-3xl"></i>
      </a>
    </div>
  );
}
